import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import NauticalGridBackground from "@/components/NauticalGridBackground"; // Import the new component

interface SelectorCardProps {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  isHovered: boolean;
  onHover: (hovered: boolean) => void;
  otherHovered: boolean;
  whatsappMessage: string;
  whatsappLabel: string;
  explorePath: string;
  exploreLabel: string;
}

const SelectorCard = ({
  title,
  subtitle,
  description,
  image,
  isHovered,
  onHover,
  otherHovered,
  whatsappMessage,
  whatsappLabel,
  explorePath,
  exploreLabel,
}: SelectorCardProps) => {
  return (
    <motion.div
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      animate={{
        flex: isHovered ? 1.5 : otherHovered ? 0.7 : 1,
      }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`relative min-h-[400px] sm:min-h-[500px] cursor-pointer overflow-hidden card-tech rounded-xl group
        ${!isHovered && !otherHovered ? 'bg-white/10 backdrop-blur-md border border-white/20' : ''}
      `}
    >
      {/* Background Image */}
      <motion.div
        animate={{
          opacity: isHovered ? 1 : 0,
          scale: isHovered ? 1 : 1.1,
        }}
        transition={{ duration: 0.6 }}
        className="absolute inset-0"
      >
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-navy-deep/40 to-transparent" />
      </motion.div>

      {/* Default State - Minimal */}
      <motion.div
        animate={{ opacity: isHovered ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 flex flex-col items-center justify-center p-8"
      >
        <span className="text-xs uppercase tracking-[0.3em] text-primary-foreground/70 mb-4">
          {subtitle}
        </span>
        <h3 className="font-heading text-2xl sm:text-3xl font-medium text-primary-foreground text-center">
          {title}
        </h3>
        <div className="mt-6 w-12 h-px bg-gold" />
      </motion.div>

      {/* Hovered State - Full Content */}
      <motion.div
        animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 20 }}
        transition={{ duration: 0.4, delay: isHovered ? 0.2 : 0 }}
        className="absolute inset-0 flex flex-col justify-end p-8 sm:p-10"
      >
        <span className="text-xs uppercase tracking-[0.3em] text-primary-foreground/70 mb-3">
          {subtitle}
        </span>
        <h3 className="font-heading text-3xl sm:text-4xl font-medium text-primary-foreground mb-4">
          {title}
        </h3>
        <p className="text-primary-foreground/80 text-sm sm:text-base leading-relaxed max-w-md">
          {description}
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-start gap-3">
          <a
            href={`https://wa.me/554792691561?text=${encodeURIComponent(whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
            </svg>
            {whatsappLabel}
          </a>
          <motion.a
            href={explorePath}
            whileHover={{ x: 5 }}
            className="inline-flex items-center gap-2 text-gold font-medium text-sm group/btn"
          >
            {exploreLabel}
            <svg
              className="w-4 h-4 transition-transform group-hover/btn:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </motion.a>
        </div>
      </motion.div>

      {/* Border Highlight */}
      <motion.div
        animate={{ opacity: isHovered ? 1 : 0 }}
        className="absolute inset-0 border-2 border-gold/50 rounded-xl pointer-events-none"
      />
    </motion.div>
  );
};

const SelectorSection = () => {
  const [hoveredCard, setHoveredCard] = useState<"left" | "right" | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const cards = [
    {
      id: "left" as const,
      title: "Quero Pilotar",
      subtitle: "Habilitação Náutica",
      description:
        "Curso completo para sua habilitação como Arrais Amador, Mestre Amador ou Capitão Amador. Aprenda com instrutores experientes e navegue com segurança.",
      image: "/assets/Aula pratica.png",
      whatsappMessage: "Olá! Quero saber mais sobre os cursos de habilitação náutica.",
      whatsappLabel: "Agendar Aula",
      explorePath: "/escola",
      exploreLabel: "Ver Cursos",
    },
    {
      id: "right" as const,
      title: "Já Tenho Barco",
      subtitle: "Despachante Náutico",
      description:
        "Regularização, documentação e toda a burocracia da sua embarcação resolvida. Transferência, registro, vistoria e muito mais com agilidade.",
      image: "/assets/Despachante.png",
      whatsappMessage: "Olá! Preciso de ajuda com a documentação da minha embarcação.",
      whatsappLabel: "Falar com Despachante",
      explorePath: "/despachante",
      exploreLabel: "Ver Serviços",
    },
  ];

  return (
    <section
      ref={ref}
      id="selector"
      className="relative bg-primary py-24 sm:py-32" // Changed bg-secondary to bg-primary
    >
      <NauticalGridBackground /> {/* Add the nautical grid background here */}
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-primary-foreground">
            Como podemos te ajudar?
          </h2>
        </motion.div>

        {/* Cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto"
        >
          {cards.map((card) => (
            <SelectorCard
              key={card.id}
              {...card}
              isHovered={hoveredCard === card.id}
              otherHovered={hoveredCard !== null && hoveredCard !== card.id}
              onHover={(hovered) =>
                setHoveredCard(hovered ? card.id : null)
              }
            />
          ))}
        </motion.div>


      </div>
    </section>
  );
};

export default SelectorSection;
